import { Link, useNavigate } from "@tanstack/react-router"
import { CircleUserRound, Send } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import { useT } from "@/i18n"
import {
  StatusBadge,
  useCreateSelfAssignmentMutation,
  useGetAssignmentsQuery,
} from "@/assignment"
import {
  TopUp,
  formatMoney,
  isInsufficientCreditsError,
  selfAssignmentAction,
  useGetBalanceQuery,
} from "@/money"
import { NotificationType } from "@/notification"
import { useCurrentTenant } from "@/tenant"
import { Button } from "@/ui/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/ui/components/ui/card"
import { Spinner } from "@/ui/components/ui/spinner"
import { formatDate } from "@/utils"

export default function DashboardPage() {
  const { t } = useT("assignment")
  const { tenantSlug, tenant } = useCurrentTenant()
  const navigate = useNavigate()
  const [createSelfAssignment, { isLoading: isCreatingSelf }] =
    useCreateSelfAssignmentMutation()
  const { data: recent } = useGetAssignmentsQuery({ page: 1, perPage: 5 })
  const { data: balance } = useGetBalanceQuery()
  const [selfTopUpOpen, setSelfTopUpOpen] = useState(false)

  const hasCredits = (balance?.available ?? 0) >= 1
  const isOwner = tenant?.permissions.billingAdmin ?? false

  const openQuestionnaire = async (assignmentId: string) =>
    navigate({ to: "/r/$assignmentId", params: { assignmentId } })

  const handleProfileYourself = async () => {
    if (hasCredits) {
      try {
        // §10.7: a credit is available — reserve it and go straight in.
        const assignment = await createSelfAssignment({}).unwrap()
        await openQuestionnaire(assignment.id)

        return
      } catch (error) {
        // The cached balance can be stale (the credit was spent in another
        // tab or session); the backend answers 402 so we fall through to the
        // no-credit path. Anything else is unexpected — let it bubble.
        if (!isInsufficientCreditsError(error)) {
          throw error
        }
      }
    }

    if (isOwner) {
      // No (or no-longer-available) credit: owners pay inline, then the
      // finalized self-assignment drops them into the questionnaire
      // (onComplete below).
      setSelfTopUpOpen(true)
    } else {
      toast.error(t("money:self.askOwner"))
    }
  }

  const selfCtaLabel = hasCredits
    ? t("money:self.ctaHasCredits")
    : t("money:self.ctaBuy", {
        price: formatMoney({
          amount: balance?.unitPriceCents ?? 0,
          currency: balance?.currency ?? "usd",
        }),
      })

  // §10.4: "Profile yourself" is the primary CTA on Personal Workspaces;
  // "Send assignments" is primary on org workspaces (the default).
  const isPersonal = tenant?.isPersonalDefault ?? false

  const sendCard = (primary: boolean) => (
    <Card>
      <CardHeader>
        <CardTitle>{t("dashboard.sendTitle")}</CardTitle>
        <CardDescription>{t("dashboard.sendDescription")}</CardDescription>
      </CardHeader>
      <CardFooter>
        <Button
          variant={primary ? "default" : "secondary"}
          render={
            <Link to="/t/$tenantSlug/assignments" params={{ tenantSlug }} />
          }
        >
          <Send />
          {t("dashboard.sendCta")}
        </Button>
      </CardFooter>
    </Card>
  )

  const selfCard = (primary: boolean) => (
    <Card>
      <CardHeader>
        <CardTitle>{t("dashboard.selfTitle")}</CardTitle>
        <CardDescription>{t("dashboard.selfDescription")}</CardDescription>
      </CardHeader>
      <CardFooter className="flex flex-col items-start gap-2">
        <Button
          variant={primary ? "default" : "secondary"}
          disabled={isCreatingSelf}
          onClick={handleProfileYourself}
        >
          {isCreatingSelf ? <Spinner /> : <CircleUserRound />}
          {selfCtaLabel}
        </Button>
        {/* Terms §9.3: show the waiver only where this click actually commits
            a Credit — not when a non-owner with no credits is bounced to "ask
            an owner" (mirrors the send form's exclusion). Issue #90. */}
        {(hasCredits || isOwner) && (
          <p className="text-xs text-muted-foreground">{t("consent.self")}</p>
        )}
      </CardFooter>
    </Card>
  )

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 py-8">
      <div className="grid gap-6 md:grid-cols-2">
        {isPersonal ? (
          <>
            {selfCard(true)}
            {sendCard(false)}
          </>
        ) : (
          <>
            {sendCard(true)}
            {selfCard(false)}
          </>
        )}
      </div>
      <Card>
        <CardHeader>
          <CardTitle>{t("dashboard.recentTitle")}</CardTitle>
        </CardHeader>
        <CardContent>
          {recent && recent.items.length > 0 ? (
            <ul className="flex flex-col divide-y">
              {recent.items.map((assignment) => (
                <li
                  key={assignment.id}
                  className="flex items-center justify-between gap-4 py-2 text-sm"
                >
                  <span className="truncate">
                    {assignment.respondent.email ?? "—"}
                  </span>
                  <span className="flex shrink-0 items-center gap-3">
                    <StatusBadge status={assignment.status} />
                    <span className="text-muted-foreground">
                      {formatDate("date", assignment.createdAt)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t("dashboard.recentEmpty")}
            </p>
          )}
        </CardContent>
        <CardFooter>
          <Link
            to="/t/$tenantSlug/assignments"
            params={{ tenantSlug }}
            className="text-sm underline underline-offset-3 hover:text-foreground"
          >
            {t("dashboard.viewAll")}
          </Link>
        </CardFooter>
      </Card>
      {isOwner && (
        <TopUp
          action={selfAssignmentAction(null)}
          shortfall={1}
          presentation="dialog"
          open={selfTopUpOpen}
          onOpenChange={setSelfTopUpOpen}
          onComplete={(settlement) => {
            // The settlement event delivers the created assignment as its
            // entityId — but only for a self-profile outcome (a failed action
            // degrades to credits-ready, which has nothing to open).
            if (settlement.type === NotificationType.SelfProfileReady) {
              void openQuestionnaire(settlement.entityId)
            }
          }}
        />
      )}
    </div>
  )
}
